import { useContext } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';

import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbRespPlaceholderTile } from '../../finding/SbRespSearchResultContainer/SbRespPlaceholderTile';

import {
    VERTICAL_OFFSET_SB_RESP_IMG,
    VERTICAL_OFFSET_MOBILE_IMG,
} from '../../constants/imageConstants';
import { locations } from '../../utils/tracking/shared/favoritesTracking';

import classnames from 'classnames';
import styles from './BuySharedResultContainer.scss';
import { SbSharedItemTrackingProvider } from '../../finding/SbSharedItemTrackingProvider/SbSharedItemTrackingProvider';

const LAZY_LOAD_ITEM_ROWS = 3;

function BuySharedResultContainerComponent({
    viewer,
    buyPage,
    user,
    relay,
    relayVariables,
    refetchInFlight,
    showBuyPageRedesign,
}) {
    const { edges: items } = buyPage;

    const { isMobile } = useContext(ServerVarsContext);

    const itemsPerRow = showBuyPageRedesign ? 4 : 3;

    return (
        <div className={styles.container}>
            <FavoritesProvider
                disable={!relayVariables.isClient}
                environment={relay.environment}
                itemIds={items.map(item => item?.node?.item?.serviceId)}
                // location should be kept the same GROWTH-4297
                location={locations.SEARCH_BROWSE}
                userId={relayVariables.userId}
                fetchFolder={!isMobile}
            >
                {({ props: favorites }) => (
                    <QuickViewProvider>
                        {({ quickView }) => (
                            <SbSharedItemTrackingProvider
                                viewer={viewer}
                                itemSearch={buyPage}
                                items={items.map(item => item?.node?.item)}
                            >
                                {({ fireItemImpressionTracking, fireItemClickTracking }) =>
                                    items.map(({ node: { item } }, index) => {
                                        if (refetchInFlight && !isMobile) {
                                            return <SbRespPlaceholderTile key={item.serviceId} />;
                                        }
                                        return (
                                            <SharedItemTile
                                                showCreatorName
                                                showSellerName={false}
                                                isMobile={isMobile}
                                                key={item.serviceId}
                                                item={item}
                                                user={user}
                                                favorites={favorites}
                                                index={index}
                                                quickView={quickView}
                                                lazyLoadStartIndex={
                                                    isMobile
                                                        ? undefined
                                                        : LAZY_LOAD_ITEM_ROWS * itemsPerRow
                                                }
                                                isClient={relayVariables.isClient}
                                                imageLoadVerticalOffset={
                                                    isMobile
                                                        ? VERTICAL_OFFSET_MOBILE_IMG
                                                        : VERTICAL_OFFSET_SB_RESP_IMG
                                                }
                                                onContentVisible={() =>
                                                    fireItemImpressionTracking({
                                                        itemId: item.serviceId,
                                                        index,
                                                    })
                                                }
                                                onClick={() =>
                                                    fireItemClickTracking({
                                                        itemId: item.serviceId,
                                                        index,
                                                    })
                                                }
                                                customClassName={classnames(styles.tileWidth, {
                                                    [styles.desktop4ItemsRow]: showBuyPageRedesign,
                                                    [styles.desktop3ItemsRow]: !showBuyPageRedesign,
                                                })}
                                                viewer={viewer}
                                                itemSearch={buyPage}
                                                fetchFolderOnMouseOver
                                            />
                                        );
                                    })
                                }
                            </SbSharedItemTrackingProvider>
                        )}
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
}

BuySharedResultContainerComponent.propTypes = {
    viewer: PropTypes.object.isRequired,
    buyPage: PropTypes.object.isRequired,
    user: PropTypes.object,
    relay: PropTypes.object.isRequired,
    showBuyPageRedesign: PropTypes.bool,

    // redux
    relayVariables: PropTypes.object.isRequired,
    refetchInFlight: PropTypes.bool,
};

function mapStateToProps(state) {
    return {
        relayVariables: state.relayVariables.variables,
        refetchInFlight: state.filters.refetchInFlight,
    };
}

export const BuySharedResultContainer = createFragmentContainer(
    connect(mapStateToProps)(BuySharedResultContainerComponent),
    {
        viewer: graphql`
            fragment BuySharedResultContainer_viewer on Viewer {
                ...SharedItemTile_viewer
                ...SbSharedItemTrackingProvider_viewer
            }
        `,
        buyPage: graphql`
            fragment BuySharedResultContainer_buyPage on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                edges {
                    node {
                        item {
                            serviceId
                            ...SbSharedItemTrackingProvider_item
                            ...SharedItemTile_item
                                @arguments(
                                    isTrade: $isTrade
                                    userCountryCode: $userCountryCode
                                    priceBookName: $priceBookName
                                    showSeller: $showSeller
                                )
                        }
                    }
                }
                ...SbSharedItemTrackingProvider_itemSearch
            }
        `,
        user: graphql`
            fragment BuySharedResultContainer_user on User {
                ...SharedItemTile_user
            }
        `,
    }
);
